import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  TextField,
  Typography,
  Switch,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Grid,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  FormHelperText,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../stores";
import {
  getProfileDetails,
  updateProfileDetails,
  changePassword,
} from "../../features/profile-module/profileAction";
import {
  resetProfileState,
  resetChangePasswordState,
} from "../../features/profile-module/profileSlice";
import { getAuthenticatedUser } from "../../features/auth-module/authAction";

const Container = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  background: "#fff",
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
  transform: "scale(0.97)",
  transformOrigin: "center",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    transform: "scale(0.95)",
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  background: "#fff",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
    minHeight: "100vh",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    justifyContent: "flex-start",
  },
}));

const Content = styled(Box)<{ component?: React.ElementType }>(({ theme }) => ({
  width: "100%",
  maxWidth: "auto",
  padding: theme.spacing(1),
  marginTop: theme.spacing(0),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0),
    maxWidth: "100%",
    marginTop: theme.spacing(0),
  },
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontSize: "30px",
  fontWeight: 600,
  color: "#4b5b5d",
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "20px",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "5px",
    backgroundColor: "#f5f5f5",
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#bdbdbd",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#4b5b5d",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#757575",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#4b5b5d",
  },
  "& .MuiOutlinedInput-input": {
    padding: "10px 12px",
    fontSize: "0.85rem",
    [theme.breakpoints.down("sm")]: {
      padding: "10px 12px",
      fontSize: "0.8rem",
    },
  },
}));

const PasswordTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#f8f9fa",
    "& fieldset": {
      borderColor: "#e9ecef",
      borderWidth: "1.5px",
    },
    "&:hover fieldset": {
      borderColor: "#4b5b5d",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#4b5b5d",
      borderWidth: "2px",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#6c757d",
    fontSize: "0.9rem",
    fontWeight: 500,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#4b5b5d",
  },
  "& .MuiOutlinedInput-input": {
    padding: "12px 14px",
    fontSize: "0.9rem",
    fontWeight: 500,
  },
}));

const SubHeading = styled(Typography)(({ theme }) => ({
  color: "#4b5b5d",
  fontSize: "20px",
  fontWeight: 600,
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "16px",
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
  },
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  color: "#455354",
  fontSize: "0.95rem",
  fontWeight: 600,
  marginBottom: theme.spacing(0.5),
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.8rem",
  },
}));

const PasswordFieldLabel = styled(Typography)(({ theme }) => ({
  color: "#4b5b5d",
  fontSize: "0.9rem",
  fontWeight: 600,
  marginBottom: theme.spacing(1),
  display: "flex",
  alignItems: "center",
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  textTransform: "none",
  fontWeight: 600,
  background: "linear-gradient(to top, #5E7273, #455354)",
  color: "#FFFFFF",
  borderRadius: "5px",
  padding: theme.spacing(1, 4),
  fontSize: "16px",
  "&:hover": {
    backgroundColor: "#354142",
  },
  "&:disabled": {
    backgroundColor: "#bdbdbd",
    color: "#fff",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.25),
    fontSize: "14px",
    minHeight: "48px",
  },
}));

const CancelButton = styled(Button)(({ theme }) => ({
  marginLeft: 10,
  width: "100%",
  display: "flex",
  justifyContent: "center",
  textTransform: "none",
  fontWeight: 600,
  background: "#F8F8F8",
  color: "#455354",
  borderRadius: "5px",
  padding: theme.spacing(1, 4),
  fontSize: "16px",
  "&:hover": {
    backgroundColor: "#F2F2F2",
  },
  "&:disabled": {
    backgroundColor: "#bdbdbd",
    color: "#fff",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.25),
    fontSize: "14px",
    minHeight: "48px",
  },
}));

const PasswordPrimaryButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  background: "linear-gradient(to top, #5E7273, #455354)",
  color: "#FFFFFF",
  borderRadius: "8px",
  padding: theme.spacing(1, 3),
  fontSize: "12px",
  minWidth: "140px",
  "&:hover": {
    backgroundColor: "#354142",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(75, 91, 93, 0.3)",
  },
  "&:disabled": {
    backgroundColor: "#bdbdbd",
    color: "#fff",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1, 2.5),
    fontSize: "14px",
    minWidth: "120px",
  },
}));

const PasswordCancelButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  background: "#f5f0e7",
  color: "#455354",
  borderRadius: "8px",
  padding: theme.spacing(1, 3),
  fontSize: "12px",
  minWidth: "140px",
  border: "1px solid #e0d9cc",
  "&:hover": {
    backgroundColor: "#ede7d9",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(75, 91, 93, 0.1)",
  },
  "&:disabled": {
    backgroundColor: "#bdbdbd",
    color: "#fff",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1, 2.5),
    fontSize: "14px",
    minWidth: "120px",
  },
}));

const Android12Switch = styled(Switch)(({ theme }) => ({
  padding: 8,
  "& .MuiSwitch-track": {
    borderRadius: 22 / 2,
    backgroundColor: "#b5b4b1",
    "&::before, &::after": {
      content: '""',
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      width: 16,
      height: 16,
    },
  },
  "& .MuiSwitch-switchBase": {
    "&.Mui-checked": {
      color: "#323c3d",
      "& + .MuiSwitch-track": {
        backgroundColor: "#323c3d",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "none",
    width: 16,
    height: 16,
    margin: 2,
    backgroundColor: "#ffffff",
  },
}));

const LoaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "400px",
  width: "100%",
  height: "100%",
}));

const LoaderText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontSize: "0.9rem",
  color: "#4b5b5d",
  fontWeight: 500,
}));

const PasswordDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
    border: "1px solid #f0f0f0",
    overflow: "hidden",
  },
}));

const PasswordDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: "linear-gradient(135deg, #f5f0e7 0%, #f8f9fa 100%)",
  padding: theme.spacing(1.5, 3),
  borderBottom: "1px solid #e9ecef",
  "& .MuiTypography-root": {
    fontSize: "1.15rem",
    fontWeight: 600,
    color: "#4b5b5d",
    textAlign: "center",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2, 2.5),
    "& .MuiTypography-root": {
      fontSize: "1.1rem",
    },
  },
}));

const PasswordDialogContent = styled(DialogContent)(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: theme.spacing(3),
  background: "#fff",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2.5),
  },
}));

const PasswordDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2.5, 3),
  background: "#f8f9fa",
  borderTop: "1px solid #e9ecef",
  gap: theme.spacing(1.5),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    flexDirection: "column",
    "& .MuiButton-root": {
      width: "100%",
    },
  },
}));

const PasswordStrengthIndicator = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

const StrengthBar = styled(Box)<{ strength: number }>(
  ({ theme, strength }) => ({
    height: "4px",
    borderRadius: "2px",
    flex: 1,
    backgroundColor:
      strength === 0
        ? "#e9ecef"
        : strength === 1
        ? "#ff6b6b"
        : strength === 2
        ? "#ffa94d"
        : strength === 3
        ? "#51cf66"
        : "#2f9e44",
    transition: "all 0.3s ease",
  })
);

const StrengthText = styled(Typography)<{ strength: number }>(
  ({ theme, strength }) => ({
    fontSize: "0.75rem",
    fontWeight: 600,
    color:
      strength === 0
        ? "#6c757d"
        : strength === 1
        ? "#ff6b6b"
        : strength === 2
        ? "#ffa94d"
        : strength === 3
        ? "#51cf66"
        : "#2f9e44",
  })
);

const RequirementList = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  backgroundColor: "#f8f9fa",
  borderRadius: "6px",
  border: "1px solid #e9ecef",
}));

const RequirementItem = styled(Typography)<{ met: boolean }>(
  ({ theme, met }) => ({
    fontSize: "0.8rem",
    color: met ? "#51cf66" : "#6c757d",
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
    "&::before": {
      content: '"•"',
      marginRight: theme.spacing(1),
      color: met ? "#51cf66" : "#6c757d",
      fontWeight: "bold",
    },
  })
);

const UserProfile = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    profile,
    loading,
    error,
    changePasswordLoading,
    changePasswordError,
  } = useSelector((state: RootState) => state.profile);
  const userId = useSelector((state: RootState) => state.auth.user?.id);

  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const userRole = userData?.role || "customer";

  const [formData, setFormData] = useState({
    firstName: profile?.firstname || "",
    lastName: profile?.lastname || "",
    email: profile?.email || "",
    phoneNumber: profile?.mobileNo || "",
    planName: profile?.planName || "",
    siteReportUsed: profile?.siteReportUsed || "",
    siteReportRemaining: profile?.siteReportRemaining || "",
    fleetReportUsed: profile?.fleetReportUsed || "",
    fleetReportRemaining: profile?.fleetReportRemaining || "",
    planExpiryDate: profile?.planExpiryDate || "",
  });
  const [imageError, setImageError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordErrors, setPasswordErrors] = useState<{
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});
  const [passwordTouched, setPasswordTouched] = useState<{
    currentPassword: boolean;
    newPassword: boolean;
    confirmPassword: boolean;
  }>({ currentPassword: false, newPassword: false, confirmPassword: false });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (userId) {
      dispatch(getProfileDetails({ id: userId }));
    }
    return () => {
      dispatch(resetProfileState());
    };
  }, [dispatch, userId]);

  useEffect(() => {
    if (profile) {
      setFormData({
        firstName: profile.firstname || "",
        lastName: profile.lastname || "",
        email: profile.email || "",
        phoneNumber: profile.mobileNo || "",
        planName: profile.planName || "",
        siteReportUsed: profile?.siteReportUsed || "",
        siteReportRemaining: profile?.siteReportRemaining || "",
        fleetReportUsed: profile?.fleetReportUsed || "",
        fleetReportRemaining: profile?.fleetReportRemaining || "",
        planExpiryDate: profile?.planExpiryDate || "",
      });
      setNotificationsEnabled(profile.emailNotification || false);
      setProfileImage(profile.profileImg || null);
    }
    if (error) {
      toast.error(error);
    }
    if (changePasswordError) {
      toast.error(changePasswordError);
    }
  }, [profile, error, changePasswordError]);

  const validatePhoneNumber = (phone: string): string | undefined => {
    if (!phone) return "Phone number is required";
    if (!/^\d{10}$/.test(phone))
      return "Phone number must be exactly 10 digits";
    return undefined;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (["firstName", "lastName"].includes(name)) {
      if (value.includes(" ")) {
        let fieldLabel = "";
        if (name === "firstName") fieldLabel = "First Name";
        else if (name === "lastName") fieldLabel = "Last Name";
        toast.error(`${fieldLabel} cannot contain spaces`);
        return;
      }
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else if (name === "phoneNumber") {
      const numericValue = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
      }));
      setPhoneTouched(true);
      setPhoneError(validatePhoneNumber(numericValue) || "");
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handlePhoneBlur = () => {
    setPhoneTouched(true);
    setPhoneError(validatePhoneNumber(formData.phoneNumber) || "");
  };

  const handleToggleChange = () => (e: React.ChangeEvent<HTMLInputElement>) => {
    setNotificationsEnabled(e.target.checked);
  };

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          const base64Length = reader.result.length;
          const maxBase64Size = (2 * 1024 * 1024 * 4) / 3;
          if (base64Length > maxBase64Size) {
            reject(new Error("Base64-encoded image exceeds 2MB limit"));
          } else {
            resolve(reader.result);
          }
        } else {
          reject(new Error("Failed to convert image to base64"));
        }
      };
      reader.onerror = () => reject(new Error("Error reading image"));
      reader.readAsDataURL(file);
    });
  };

  const validateImage = (file: File | null): string => {
    if (!file) return "";
    if (file.size > 2 * 1024 * 1024) return "Image must be under 2MB";
    if (!["image/png", "image/jpeg"].includes(file.type))
      return "Image must be PNG or JPEG";
    return "";
  };

  const handleProfileImageChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const imageError = validateImage(file);
    setImageError(imageError);
    if (imageError) {
      if (imageError.includes("2MB")) {
        toast.error("File too large: Please select an image smaller than 2MB");
      } else {
        toast.error(imageError);
      }
      return;
    }

    try {
      const base64Image = await convertToBase64(file);
      setProfileImage(base64Image);
      toast.success("Profile image uploaded successfully!");
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to process image";
      setImageError(errorMessage);
      if (errorMessage.includes("2MB")) {
        toast.error("File too large: Please select an image smaller than 2MB");
      } else {
        toast.error(errorMessage);
      }
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userId) {
      toast.error("User not logged in");
      return;
    }

    const phoneError = validatePhoneNumber(formData.phoneNumber);
    if (phoneError) {
      setPhoneError(phoneError);
      setPhoneTouched(true);
      return;
    }

    if (imageError) {
      toast.error(imageError);
      return;
    }

    const payload = {
      firstname: formData.firstName || undefined,
      lastname: formData.lastName || undefined,
      mobileNo: formData.phoneNumber || undefined,
      emailNotification: notificationsEnabled,
      profileImg: profileImage || undefined,
    };

    const resultAction = await dispatch(
      updateProfileDetails({ id: userId, formData: payload })
    );

    if (updateProfileDetails.fulfilled.match(resultAction)) {
      setImageError("");
      setPhoneError("");
      setPhoneTouched(false);
      await dispatch(getAuthenticatedUser()).unwrap();
    }
  };

  const handleReset = () => {
    if (profile) {
      setFormData({
        firstName: profile.firstname || "",
        lastName: profile.lastname || "",
        email: profile.email || "",
        phoneNumber: profile.mobileNo || "",
        planName: profile.planName || "",
        siteReportUsed: profile?.siteReportUsed || "",
        siteReportRemaining: profile?.siteReportRemaining || "",
        fleetReportUsed: profile?.fleetReportUsed || "",
        fleetReportRemaining: profile?.fleetReportRemaining || "",
        planExpiryDate: profile?.planExpiryDate || "",
      });
      setNotificationsEnabled(profile.emailNotification || false);
      setProfileImage(profile.profileImg || null);
      setImageError("");
      setPhoneError("");
      setPhoneTouched(false);
    }
  };

  // Format date for display in MM-DD-YY format
  const formatExpiryDate = (expiryDate: string | undefined) => {
    if (!expiryDate) return "No expiry date";

    try {
      const date = new Date(expiryDate);
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const year = String(date.getFullYear()).slice(-2);
      return `${month}-${day}-${year}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid date";
    }
  };

  const openChangePassword = () => {
    setIsChangePasswordOpen(true);
  };

  const closeChangePassword = () => {
    setIsChangePasswordOpen(false);
    setPasswordForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setPasswordErrors({});
    setPasswordTouched({
      currentPassword: false,
      newPassword: false,
      confirmPassword: false,
    });
    setPasswordStrength(0);
    dispatch(resetChangePasswordState());
  };

  const validateCurrentPassword = (value: string): string | undefined => {
    if (!value) return "Please enter your current password";
    if (value.includes(" ")) return "Password cannot contain spaces";
    if (value.length < 8) return "Password must be at least 8 characters long";
    return undefined;
  };

  const calculatePasswordStrength = (password: string): number => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[!@#$%^&*(),.?":{}|<>_-]/.test(password)) strength++;
    return Math.min(strength, 5);
  };

  const validateNewPassword = (value: string): string | undefined => {
    if (!value) return "Please create a new password";
    if (value.includes(" ")) return "Password cannot contain spaces";

    const requirements = [];

    if (value.length < 8) requirements.push("at least 8 characters");
    if (!/[a-z]/.test(value)) requirements.push("one lowercase letter");
    if (!/[A-Z]/.test(value)) requirements.push("one uppercase letter");
    if (!/[0-9]/.test(value)) requirements.push("one number");
    if (!/[!@#$%^&*(),.?":{}|<>_-]/.test(value))
      requirements.push("one special character");

    if (requirements.length > 0) {
      return `Must contain: ${requirements.join(", ")}`;
    }

    return undefined;
  };

  const validateConfirmPassword = (
    confirm: string,
    pwd: string
  ): string | undefined => {
    if (!confirm) return "Please confirm your new password";
    if (confirm !== pwd) return "Passwords do not match. Please try again.";
    return undefined;
  };

  const runPasswordValidation = (
    field?: "currentPassword" | "newPassword" | "confirmPassword"
  ) => {
    const nextErrors: {
      currentPassword?: string;
      newPassword?: string;
      confirmPassword?: string;
    } = {
      ...passwordErrors,
    };

    const { currentPassword, newPassword, confirmPassword } = passwordForm;

    // Always validate all fields when no specific field is provided
    if (!field) {
      nextErrors.currentPassword = validateCurrentPassword(currentPassword);
      nextErrors.newPassword = validateNewPassword(newPassword);
      nextErrors.confirmPassword = validateConfirmPassword(
        confirmPassword,
        newPassword
      );
    } else {
      // Validate specific field when provided
      if (field === "currentPassword") {
        nextErrors.currentPassword = validateCurrentPassword(currentPassword);
      }
      if (field === "newPassword") {
        nextErrors.newPassword = validateNewPassword(newPassword);
      }
      if (field === "confirmPassword") {
        nextErrors.confirmPassword = validateConfirmPassword(
          confirmPassword,
          newPassword
        );
      }
    }

    setPasswordErrors(nextErrors);
    return nextErrors;
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target as {
      name: "currentPassword" | "newPassword" | "confirmPassword";
      value: string;
    };
    if (value.includes(" ")) {
      toast.error("Password cannot contain spaces");
      return;
    }
    setPasswordForm((prev) => ({ ...prev, [name]: value }));

    if (name === "newPassword") {
      setPasswordStrength(calculatePasswordStrength(value));
    }

    setTimeout(() => runPasswordValidation(name), 0);
  };

  const handlePasswordBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target as {
      name: "currentPassword" | "newPassword" | "confirmPassword";
    };
    setPasswordTouched((prev) => ({ ...prev, [name]: true }));
    runPasswordValidation(name);
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched when submitting
    setPasswordTouched({
      currentPassword: true,
      newPassword: true,
      confirmPassword: true,
    });

    const errs = runPasswordValidation();
    const hasError = Boolean(
      errs.currentPassword || errs.newPassword || errs.confirmPassword
    );
    if (hasError) {
      toast.error("Please fix the errors before submitting");
      return;
    }

    const resultAction = await dispatch(
      changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      })
    );

    if (changePassword.fulfilled.match(resultAction)) {
      closeChangePassword();
      toast.success("Password updated successfully!");
    }
  };

  const getPasswordRequirements = () => {
    const password = passwordForm.newPassword;
    return [
      {
        text: "Minimum 8 characters",
        met: password.length >= 8,
      },
      {
        text: "At least one lowercase letter (a-z)",
        met: /[a-z]/.test(password),
      },
      {
        text: "At least one uppercase letter (A-Z)",
        met: /[A-Z]/.test(password),
      },
      {
        text: "At least one number (0-9)",
        met: /[0-9]/.test(password),
      },
      {
        text: "At least one special character (!@#$%^&* etc.)",
        met: /[!@#$%^&*(),.?":{}|<>_-]/.test(password),
      },
    ];
  };

  const getStrengthLabel = (strength: number) => {
    switch (strength) {
      case 0:
        return "Very Weak - Add more characters and requirements";
      case 1:
        return "Weak - Add more character types";
      case 2:
        return "Fair - Getting stronger";
      case 3:
        return "Good - Strong password";
      case 4:
        return "Strong - Very secure";
      case 5:
        return "Very Strong - Excellent password security";
      default:
        return "Very Weak - Add more characters and requirements";
    }
  };

  return (
    <Container>
      <FormSection>
        {loading ? (
          <LoaderContainer>
            <CircularProgress
              size={40}
              thickness={4}
              sx={{
                color: "#4b5b5d",
              }}
            />
            <LoaderText>Loading profile details...</LoaderText>
          </LoaderContainer>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            <Content component="form">
              <WelcomeText>Profile</WelcomeText>
              <Box
                sx={{
                  background:
                    "linear-gradient(135deg, #edefee 0%, #fcfbf9 100%)",
                  padding: isMobile ? 2 : 2.5,
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    padding: isMobile ? 2 : 3,
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: isMobile ? "center" : "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: isMobile ? 88 : 112,
                      height: isMobile ? 88 : 112,
                      mr: isMobile ? 0 : 2,
                      mb: isMobile ? 2 : 0,
                    }}
                  >
                    <Avatar
                      alt={`${formData.firstName} ${formData.lastName}`}
                      src={profileImage || undefined}
                      sx={{
                        width: "100%",
                        height: "100%",
                        border: "2px solid #455354",
                        boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
                        bgcolor: "#e7eded",
                        color: "#455354",
                        fontWeight: 700,
                        fontSize: isMobile ? 20 : 26,
                      }}
                    >
                      {formData.firstName?.[0]}
                      {formData.lastName?.[0]}
                    </Avatar>
                    <input
                      accept="image/png, image/jpeg"
                      id="profile-image-input"
                      type="file"
                      onChange={handleProfileImageChange}
                      style={{ display: "none" }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: isMobile ? "center" : "flex-start",
                      textAlign: isMobile ? "center" : "left",
                    }}
                  >
                    <label htmlFor="profile-image-input">
                      <Button
                        component="span"
                        variant="contained"
                        sx={{
                          textTransform: "none",
                          fontSize: isMobile ? "14px" : "16px",
                          padding: isMobile ? "6px 12px" : "8px 16px",
                          backgroundColor: "#FFFFFF",
                          color: "#455354",
                          "&:hover": {
                            backgroundColor: "rgba(69,83,84,0.06)",
                          },
                        }}
                      >
                        Upload a New Photo
                      </Button>
                    </label>
                    <Typography
                      variant="caption"
                      mt={1}
                      sx={{
                        fontSize: isMobile ? "12px" : "14px",
                        color: "#8F8F8F",
                      }}
                    >
                      At least 800*800px recommended. JPG or PNG is allowed. Max
                      file size: 2MB.
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  onClick={openChangePassword}
                  sx={{
                    background: "linear-gradient(to bottom, #F5F0E7, #CDC8BE)",
                    color: "#455354",
                    position: { xs: "static", sm: "absolute" },
                    top: { sm: 15 },
                    right: { sm: 15, md: 25 },
                    textTransform: "none",
                    fontSize: isMobile ? "14px" : "14px",
                    fontWeight: 600,
                    padding: isMobile ? "6px 12px" : "6px 12px",
                    width: { xs: "100%", sm: "auto" },
                    mb: { xs: 1.5, sm: 0 },
                  }}
                >
                  Change Password
                </Button>

                <Divider sx={{ my: isMobile ? 1.5 : 2 }} />

                <SubHeading>Personal Info</SubHeading>
                <Grid
                  container
                  spacing={isMobile ? 1.5 : 2}
                  sx={{ width: "100%", mt: 1 }}
                >
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FieldLabel>First Name</FieldLabel>
                    <StyledTextField
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      variant="outlined"
                      size={isMobile ? "small" : "medium"}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FieldLabel>Last Name</FieldLabel>
                    <StyledTextField
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      variant="outlined"
                      size={isMobile ? "small" : "medium"}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FieldLabel>Email</FieldLabel>
                    <StyledTextField
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      variant="outlined"
                      size={isMobile ? "small" : "medium"}
                      disabled
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FieldLabel>Phone Number</FieldLabel>
                    <StyledTextField
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      onBlur={handlePhoneBlur}
                      variant="outlined"
                      size={isMobile ? "small" : "medium"}
                      inputProps={{ maxLength: 10 }}
                      error={phoneTouched && !!phoneError}
                    />
                    {phoneTouched && phoneError && (
                      <FormHelperText error>{phoneError}</FormHelperText>
                    )}
                  </Grid>
                </Grid>

                {userRole === "customer" && (
                  <>
                    <Divider sx={{ my: isMobile ? 1.5 : 2 }} />
                    <SubHeading>Plan</SubHeading>
                    <Grid
                      container
                      spacing={isMobile ? 1.5 : 2}
                      sx={{ width: "100%", mt: 1 }}
                    >
                      <Grid size={{ xs: 12 }}>
                        <StyledTextField
                          multiline
                          value={`Current Plan: ${
                            profile?.planName || "No active plan"
                          }\nSite Reports Used: ${
                            profile?.siteReportUsed || 0
                          }\nSite Reports Remaining: ${
                            profile?.siteReportRemaining || 0
                          }\nFleet Reports Used: ${
                            profile?.fleetReportUsed || 0
                          }\nFleet Reports Remaining: ${
                            profile?.fleetReportRemaining || 0
                          }\nPlan Expiry Date: ${formatExpiryDate(
                            profile?.planExpiryDate
                          )}`}
                          variant="outlined"
                          InputProps={{ readOnly: true }}
                          size={isMobile ? "small" : "medium"}
                          sx={{ mb: 2, fontWeight: 600 }}
                          disabled
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                <Divider sx={{ my: isMobile ? 1.5 : 2 }} />

                <SubHeading>Notifications</SubHeading>
                <Grid
                  container
                  spacing={isMobile ? 1.5 : 2}
                  sx={{ width: "100%", mt: 1, alignItems: "center" }}
                >
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FieldLabel>Email</FieldLabel>
                    <StyledTextField
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      variant="outlined"
                      size={isMobile ? "small" : "medium"}
                      disabled
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Android12Switch
                              checked={notificationsEnabled}
                              onChange={handleToggleChange()}
                              size={isMobile ? "small" : "medium"}
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ width: "100%" }}
                    />
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: 2,
                  }}
                >
                  <motion.div
                    whileHover={{
                      scale: 1.04,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <PrimaryButton
                      type="submit"
                      variant="contained"
                      onClick={handleSubmit}
                    >
                      Save Profile Setting
                    </PrimaryButton>
                  </motion.div>
                  <motion.div
                    whileHover={{
                      scale: 1.04,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <CancelButton
                      type="reset"
                      variant="contained"
                      onClick={handleReset}
                    >
                      Cancel
                    </CancelButton>
                  </motion.div>
                </Box>
              </Box>
            </Content>
          </motion.div>
        )}
      </FormSection>

      {/* Enhanced Change Password Dialog */}
      <PasswordDialog
        open={isChangePasswordOpen}
        onClose={closeChangePassword}
        fullWidth
        maxWidth="sm"
      >
        <PasswordDialogTitle>Change Password</PasswordDialogTitle>
        <PasswordDialogContent>
          <Box component="form" onSubmit={handlePasswordSubmit} noValidate>
            <PasswordFieldLabel>Current Password</PasswordFieldLabel>
            <PasswordTextField
              name="currentPassword"
              type={showCurrentPassword ? "text" : "password"}
              value={passwordForm.currentPassword}
              onChange={handlePasswordChange}
              onBlur={handlePasswordBlur}
              error={Boolean(
                passwordTouched.currentPassword &&
                  passwordErrors.currentPassword
              )}
              helperText={
                passwordTouched.currentPassword
                  ? passwordErrors.currentPassword
                  : ""
              }
              variant="outlined"
              placeholder="Enter your current password to verify"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip
                      title={
                        showCurrentPassword ? "Hide password" : "Show password"
                      }
                    >
                      <IconButton
                        aria-label="toggle current password visibility"
                        onClick={() => setShowCurrentPassword((prev) => !prev)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                        size={isMobile ? "small" : "medium"}
                        sx={{ color: "#455354" }}
                      >
                        {showCurrentPassword ? (
                          <Visibility />
                        ) : (
                          <VisibilityOff />
                        )}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />

            <PasswordFieldLabel>New Password</PasswordFieldLabel>
            <PasswordTextField
              name="newPassword"
              type={showNewPassword ? "text" : "password"}
              value={passwordForm.newPassword}
              onChange={handlePasswordChange}
              onBlur={handlePasswordBlur}
              error={Boolean(
                passwordTouched.newPassword && passwordErrors.newPassword
              )}
              helperText={
                passwordTouched.newPassword ? passwordErrors.newPassword : ""
              }
              variant="outlined"
              placeholder="Create a strong, secure password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip
                      title={
                        showNewPassword ? "Hide password" : "Show password"
                      }
                    >
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowNewPassword((prev) => !prev)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                        size={isMobile ? "small" : "medium"}
                        sx={{ color: "#455354" }}
                      >
                        {showNewPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />

            {/* Password Strength Indicator */}
            {passwordForm.newPassword && (
              <PasswordStrengthIndicator>
                <StrengthBar strength={passwordStrength} />
                <StrengthText strength={passwordStrength}>
                  {getStrengthLabel(passwordStrength)}
                </StrengthText>
              </PasswordStrengthIndicator>
            )}

            {/* Password Requirements */}
            {passwordForm.newPassword && (
              <RequirementList>
                {getPasswordRequirements().map((req, index) => (
                  <RequirementItem key={index} met={req.met}>
                    {req.text}
                  </RequirementItem>
                ))}
              </RequirementList>
            )}

            <PasswordFieldLabel>Confirm Password</PasswordFieldLabel>
            <PasswordTextField
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={passwordForm.confirmPassword}
              onChange={handlePasswordChange}
              onBlur={handlePasswordBlur}
              error={Boolean(
                passwordTouched.confirmPassword &&
                  passwordErrors.confirmPassword
              )}
              helperText={
                passwordTouched.confirmPassword
                  ? passwordErrors.confirmPassword
                  : ""
              }
              variant="outlined"
              placeholder="Re-enter your new password to confirm"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip
                      title={
                        showConfirmPassword ? "Hide password" : "Show password"
                      }
                    >
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                        size={isMobile ? "small" : "medium"}
                        sx={{ color: "#455354" }}
                      >
                        {showConfirmPassword ? (
                          <Visibility />
                        ) : (
                          <VisibilityOff />
                        )}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </PasswordDialogContent>
        <PasswordDialogActions>
          <PasswordCancelButton
            onClick={closeChangePassword}
            variant="contained"
            disabled={changePasswordLoading}
          >
            Cancel
          </PasswordCancelButton>
          <PasswordPrimaryButton
            type="submit"
            variant="contained"
            onClick={handlePasswordSubmit}
            disabled={changePasswordLoading}
          >
            {changePasswordLoading ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              "Update Password"
            )}
          </PasswordPrimaryButton>
        </PasswordDialogActions>
      </PasswordDialog>
    </Container>
  );
};

export default UserProfile;
